import {
    FC,
    useContext,
    useEffect,
    useLayoutEffect,
    useState,
    useMemo,
    useCallback
} from "react";
import { ISideBarSubproject } from "../../../common/api_interfaces/common/ISideBar";
import { Link } from "react-router-dom";
import SubprojectIcon from "../../../assets/subproject.svg";
import SubprojectIconBlue from "../../../assets/subproject_blue.svg";
import BoardComponent from "./BoardComponent";
import "./NavigationPanel.css";
import ArrowButton from "../../buttons/ArrowButton/ArrowButton";
import { AppContext } from "../../../App";
import Tooltip from "../../UiLib/CustomTooltip/CustomTooltip";

interface ISubprojectComponentProps {
    subproject: ISideBarSubproject;
    showProxyBoards: boolean;
}

const SubprojectComponent: FC<ISubprojectComponentProps> = ({
    subproject,
    showProxyBoards,
}) => {
    const { toggleProject, navBarInfo } = useContext(AppContext);
    const [showTooltip, setShowTooltip] = useState<boolean>(false);

    const highlighted = useMemo(() => 
        navBarInfo.subprojectId === subproject.id,
        [navBarInfo.subprojectId, subproject.id]
    );

    const isOpen = useMemo(() => 
        navBarInfo.openProjectIds?.includes(subproject.id),
        [navBarInfo.openProjectIds, subproject.id]
    );

    const hasBoards = useMemo(() => 
        subproject.boards && subproject.boards.length > 0,
        [subproject.boards]
    );

    const handleClick = useCallback(() => {
        if (highlighted) {
            toggleProject(subproject.id);
        }
    }, [highlighted, toggleProject, subproject.id]);

    const handleArrowClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        toggleProject(subproject.id);
    }, [toggleProject, subproject.id]);

    const subprojectIcon = useMemo(() => 
        highlighted ? SubprojectIconBlue : SubprojectIcon,
        [highlighted]
    );

    const arrowDirection = useMemo(() => 
        isOpen ? "top" : "bottom",
        [isOpen]
    );

    useEffect(() => {
        const container = document.getElementById(
            `nav-panel-project-${subproject.id}`
        );
        const name = document.getElementById(
            `nav-panel-project-name-${subproject.id}`
        );

        if (container && name && name.offsetWidth > container.clientWidth) {
            setShowTooltip(true);
        }
    }, [subproject.id, subproject.name]);

    useLayoutEffect(() => {
        const container = document.getElementById(
            `nav-panel-project-${subproject.id}`
        );
        const name = document.getElementById(
            `nav-panel-project-name-${subproject.id}`
        );

        if (container && name && name.offsetWidth > container.clientWidth) {
            setShowTooltip(true);
        }
    }, [subproject.id, subproject.name]);

    const boardsContent = useMemo(() => {
        if (!isOpen || !subproject.boards?.length) return null;

        const filteredBoards = subproject.boards.filter(
            b => showProxyBoards || !b.isProxyBoard
        );

        if (!filteredBoards.length) return null;

        return (
            <div className="nav-panel_subproject_children">
                {filteredBoards.map((board) => (
                    <div
                        className="nav-panel_subproject-board"
                        key={board.id}
                    >
                        <BoardComponent
                            board={board}
                            showProxyBoards={showProxyBoards}
                        />
                    </div>
                ))}
            </div>
        );
    }, [isOpen, subproject.boards, showProxyBoards]);

    return (
        <div className="nav-panel_subproject-container">
            <Tooltip
                text={subproject.name ?? ""}
                offset={5}
                placement={"right"}
                style={{
                    width: "fit-content",
                    maxWidth: "250px",
                    wordBreak: "break-all",
                    display: showTooltip ? "block" : "none",
                }}
            >
                <Link
                    className={`nav-panel_subproject ${
                        highlighted ? "highlighted" : ""
                    } ${isOpen ? "open" : ""}`}
                    to={"/project/" + subproject.id}
                    onClick={handleClick}
                >
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <img
                            className="nav-panel_suproject-image"
                            src={subprojectIcon}
                            alt="subproject-icon"
                        />
                        <div
                            id={`nav-panel-project-${subproject.id}`}
                            className="nav-panel_label"
                        >
                            <span
                                id={`nav-panel-project-name-${subproject.id}`}
                            >
                                {subproject.name}
                            </span>
                        </div>
                    </div>
                    {hasBoards && (
                        <ArrowButton
                            onClick={handleArrowClick}
                            direction={arrowDirection}
                        />
                    )}
                </Link>
            </Tooltip>
            {boardsContent}
        </div>
    );
};

export default SubprojectComponent;